import { useState } from 'react'
import * as api from 'api/entityApi'
import { TabIcon, activityIconName } from 'components/common/TabIcons'
import { useToast } from 'components/common/Toast'
import type { ActivityItem, AppointmentItem, CallLogItem, ChatLogItem, DocumentItem, EntityType, FollowUpItem, NoteItem, TaskItem, WebformItem } from 'types/entity'
import { DataTable, ListPanel } from './ListPanel'

interface PanelProps {
  entityId: string
  entityType: EntityType
}

/**
 * Activity row icon — the legacy feed rendered the per-event image the API
 * sends in `EventIconURL` (`<div class="icon-cont"><img …>`). That image lives
 * on the Kit19 site, so when it is missing or blocked the matching inline glyph
 * is drawn instead and the row never loses its marker.
 */
function ActivityIcon({ item }: { item: ActivityItem }) {
  const [failed, setFailed] = useState(false)
  const showImage = !!item.iconUrl && !failed

  return (
    <span className="activity-icon" title={item.type}>
      {showImage ? (
        <img src={item.iconUrl} alt="" onError={() => setFailed(true)} />
      ) : (
        <TabIcon name={activityIconName(item.type, item.title)} size={16} />
      )}
    </span>
  )
}

export function ActivitiesPanel({ entityId }: PanelProps) {
  return (
    <ListPanel<ActivityItem>
      fetcher={(signal) => api.fetchActivities(entityId, signal)}
      deps={[entityId]}
      emptyMessage="No activity recorded yet."
    >
      {(items) => (
        <ul className="activity-list">
          {items.map((item) => (
            <li key={item.id}>
              <ActivityIcon item={item} />
              <div className="activity-body">
                <strong>{item.title}</strong>
                <p>{item.description}</p>
                <span className="activity-meta">
                  {item.createdOn} · {item.createdBy}
                </span>
              </div>
            </li>
          ))}
        </ul>
      )}
    </ListPanel>
  )
}

export function CallsPanel({ entityId, entityType }: PanelProps) {
  return (
    <ListPanel<CallLogItem>
      fetcher={(signal) => api.fetchCallLogs(entityId, entityType === 'enquiry' ? 'enquiry' : 'lead', signal)}
      deps={[entityId, entityType]}
      emptyMessage="No calls logged for this record."
    >
      {(items) => (
        <DataTable headers={['Call Details', 'Call time', 'Duration', 'Agent', 'Recording']}>
          {items.map((call) => (
            <tr key={call.id}>
              <td>
                <strong>{call.callType}</strong>
                <div className="text-muted font12">
                  {call.fromNumber} → {call.toNumber}
                </div>
              </td>
              <td>{call.callTime}</td>
              <td>{call.duration}</td>
              <td>{call.agent}</td>
              <td>
                {call.recordingUrl ? (
                  <audio controls src={call.recordingUrl} style={{ height: 30 }} />
                ) : (
                  <span className="text-muted">—</span>
                )}
              </td>
            </tr>
          ))}
        </DataTable>
      )}
    </ListPanel>
  )
}

export function FollowUpPanel({ entityId }: PanelProps) {
  return (
    <ListPanel<FollowUpItem>
      fetcher={(signal) => api.fetchFollowUps(entityId, signal)}
      deps={[entityId]}
      emptyMessage="No follow-ups scheduled."
    >
      {(items) => (
        <DataTable headers={['Subject', 'Follow-up date', 'Status', 'Assigned to', 'Remarks']}>
          {items.map((f) => (
            <tr key={f.id}>
              <td>{f.subject}</td>
              <td>{f.followUpDate}</td>
              <td>{f.status}</td>
              <td>{f.assignedTo}</td>
              <td>{f.remarks}</td>
            </tr>
          ))}
        </DataTable>
      )}
    </ListPanel>
  )
}

export function NotesPanel({ entityId }: PanelProps) {
  return (
    <ListPanel<NoteItem>
      fetcher={(signal) => api.fetchNotes(entityId, signal)}
      deps={[entityId]}
      emptyMessage="No notes added."
    >
      {(items) => (
        <ul className="activity-list">
          {items.map((note) => (
            <li key={note.id}>
              <div className="activity-body">
                <p>{note.note}</p>
                <span className="activity-meta">
                  {note.createdOn} · {note.createdBy}
                </span>
              </div>
            </li>
          ))}
        </ul>
      )}
    </ListPanel>
  )
}

/** Documents tab, including the delete flow from `OnClickDocumentDelete`. */
export function DocumentsPanel({ entityId }: PanelProps) {
  const toast = useToast()
  const [deletedIds, setDeletedIds] = useState<string[]>([])
  const [busyId, setBusyId] = useState<string | null>(null)

  const handleDelete = async (id: string, fileName: string) => {
    if (!window.confirm(`Delete "${fileName}"?`)) return
    setBusyId(id)
    try {
      await api.deleteDocument(id, entityId, fileName)
      setDeletedIds((prev) => [...prev, id])
      toast.push('success', 'Document deleted.')
    } catch (err) {
      toast.push('error', `Could not delete the document: ${(err as Error).message}`)
    } finally {
      setBusyId(null)
    }
  }

  return (
    <ListPanel<DocumentItem>
      fetcher={(signal) => api.fetchDocuments(entityId, signal)}
      deps={[entityId]}
      emptyMessage="No documents uploaded."
    >
      {(items) => {
        const visible = items.filter((d) => !deletedIds.includes(d.id))
        if (visible.length === 0) return <div className="empty-state">No documents uploaded.</div>

        return (
          <DataTable headers={['File', 'Uploaded on', 'Uploaded by', '']}>
            {visible.map((doc) => (
              <tr key={doc.id}>
                <td>
                  <a href={doc.filePath} target="_blank" rel="noreferrer">
                    {doc.fileName}
                  </a>
                </td>
                <td>{doc.uploadedOn}</td>
                <td>{doc.uploadedBy}</td>
                <td className="text-right">
                  <button
                    type="button"
                    className="btn btn-icon btn-danger"
                    disabled={busyId === doc.id}
                    onClick={() => handleDelete(doc.id, doc.fileName)}
                    title="Delete document"
                  >
                    ✕
                  </button>
                </td>
              </tr>
            ))}
          </DataTable>
        )
      }}
    </ListPanel>
  )
}

export function TasksPanel({ entityId }: PanelProps) {
  return (
    <ListPanel<TaskItem>
      fetcher={(signal) => api.fetchTasks(entityId, signal)}
      deps={[entityId]}
      emptyMessage="No tasks linked to this record."
    >
      {(items) => (
        <DataTable headers={['Subject', 'Due date', 'Priority', 'Status', 'Assigned to']}>
          {items.map((task) => (
            <tr key={task.id}>
              <td>{task.subject}</td>
              <td>{task.dueDate}</td>
              <td>{task.priority}</td>
              <td>{task.status}</td>
              <td>{task.assignedTo}</td>
            </tr>
          ))}
        </DataTable>
      )}
    </ListPanel>
  )
}

export function AppointmentsPanel({ entityId }: PanelProps) {
  return (
    <ListPanel<AppointmentItem>
      fetcher={(signal) => api.fetchAppointments(entityId, signal)}
      deps={[entityId]}
      emptyMessage="No appointments scheduled."
    >
      {(items) => (
        <DataTable headers={['Subject', 'Starts', 'Ends', 'Location', 'Status', 'Assigned to']}>
          {items.map((ap) => (
            <tr key={ap.id}>
              <td>{ap.subject}</td>
              <td>{ap.startDate}</td>
              <td>{ap.endDate}</td>
              <td>{ap.location}</td>
              <td>{ap.status}</td>
              <td>{ap.assignedTo}</td>
            </tr>
          ))}
        </DataTable>
      )}
    </ListPanel>
  )
}

export function ChatPanel({ entityId }: PanelProps) {
  return (
    <ListPanel<ChatLogItem>
      fetcher={(signal) => api.fetchChatLogs(entityId, signal)}
      deps={[entityId]}
      emptyMessage="No chat history."
    >
      {(items) => (
        <div className="chat-log">
          {items.map((chat) => (
            <div key={chat.id} className={`chat-bubble ${chat.direction}`}>
              {chat.message}
              <span className="meta">
                {chat.sender} · {chat.sentOn}
              </span>
            </div>
          ))}
        </div>
      )}
    </ListPanel>
  )
}

export function WebformPanel({ entityId }: PanelProps) {
  return (
    <ListPanel<WebformItem>
      fetcher={(signal) => api.fetchWebforms(entityId, signal)}
      deps={[entityId]}
      emptyMessage="No webform submissions."
    >
      {(items) => (
        <DataTable headers={['Form', 'Submitted on', 'Submitted by']}>
          {items.map((form) => (
            <tr key={form.id}>
              <td>{form.formName}</td>
              <td>{form.submittedOn}</td>
              <td>{form.submittedBy}</td>
            </tr>
          ))}
        </DataTable>
      )}
    </ListPanel>
  )
}
